import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Separator } from "../components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import Navigation from "../components/layout/Navigation";
import Footer from "../components/layout/Footer";
import PointsDashboard from "../components/ambassador/PointsDashboard";
import MissionsHub from "../components/ambassador/MissionsHub";
import ResourceLibrary from "../components/ambassador/ResourceLibrary";
import DashboardTutorial, { useTutorialState } from "../components/ambassador/DashboardTutorial";
import { toast } from "sonner";
import { 
  Copy, DollarSign, Users, TrendingUp, Share2, 
  ExternalLink, Facebook, Twitter, Mail, CheckCircle,
  Clock, ArrowUpRight, Gift, Award, Coins, QrCode, Download,
  Target, BookOpen, HelpCircle
} from "lucide-react";
import { API } from "../apiConfig";


const PromoterDashboardPage = () => {
  const { user, token } = useAuth();
  const [stats, setStats] = useState(null);
  const [referrals, setReferrals] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [qrData, setQrData] = useState(null);
  const [showQrModal, setShowQrModal] = useState(false);
  
  // Tutorial state
  const { showTutorial, startTutorial, endTutorial, resetTutorial } = useTutorialState();

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, referralsRes, payoutsRes] = await Promise.all([
        axios.get(`${API}/promoter/stats`, { headers }),
        axios.get(`${API}/promoter/referrals`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/promoter/payouts`, { headers }).catch(() => ({ data: [] }))
      ]);
      setStats(statsRes.data);
      setReferrals(referralsRes.data);
      setPayouts(payoutsRes.data);
    } catch (error) {
      console.error("Error fetching stats:", error);
    } finally {
      setLoading(false);
    }
  };

  const referralCode = user?.referral_code || stats?.referral_code || "";
  const referralLink = `${window.location.origin}?ref=${referralCode}`;

  const copyReferralLink = () => {
    navigator.clipboard.writeText(referralLink);
    toast.success("Referral link copied!");
  };

  const copyReferralCode = () => {
    navigator.clipboard.writeText(referralCode);
    toast.success("Referral code copied!");
  };

  const shareOnFacebook = () => {
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(referralLink)}`, '_blank');
  };

  const shareOnTwitter = () => {
    const text = "Discover beautiful Aboriginal art from Colin Isaacs at Art By Arawol! Use my link for a special offer:";
    window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(referralLink)}`, '_blank');
  };

  const shareByEmail = () => {
    const subject = "Discover Art By Arawol - Aboriginal Art";
    const body = `Hi,\n\nI wanted to share this amazing collection of Aboriginal art by Colin Isaacs.\n\nCheck it out here: ${referralLink}\n\nEnjoy!`;
    window.location.href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  const fetchQrCode = async () => {
    try {
      const res = await axios.get(`${API}/promoter/qr-data`, { headers });
      setQrData(res.data);
      setShowQrModal(true);
    } catch (error) {
      toast.error("Failed to generate QR code");
    }
  };

  const downloadQrCode = () => {
    if (!qrData?.qr_data_url) return;
    const link = document.createElement('a');
    link.href = qrData.qr_data_url;
    link.download = `arawol-referral-${referralCode}.png`;
    link.click();
    toast.success("QR code downloaded!");
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD'
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-AU', { 
      year: 'numeric', month: 'short', day: 'numeric' 
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen">
        <Navigation />
        <div className="pt-32 pb-24 px-6 md:px-12">
          <div className="max-w-5xl mx-auto animate-pulse space-y-6">
            <div className="h-12 bg-muted rounded w-1/3" />
            <div className="grid grid-cols-4 gap-4">
              {[...Array(4)].map((_, i) => <div key={i} className="h-32 bg-muted rounded-2xl" />)}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="promoter-dashboard">
      <Navigation />
      
      {/* Dashboard Tutorial */}
      {showTutorial && (
        <DashboardTutorial 
          onComplete={endTutorial}
          onDismiss={endTutorial}
        />
      )}

      <section className="pt-32 pb-24 px-6 md:px-12">
        <div className="max-w-5xl mx-auto">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8"
          >
            <div className="flex items-center justify-between mb-4">
              <Badge variant="outline">Ambassador Program</Badge>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={resetTutorial}
                className="text-muted-foreground hover:text-foreground"
                data-testid="tutorial-help-btn"
              >
                <HelpCircle className="w-4 h-4 mr-1" />
                Take Tour
              </Button>
            </div>
            <h1 className="text-4xl md:text-5xl font-medium mb-4" data-testid="promoter-title">
              Welcome, {user?.name?.split(' ')[0] || 'Ambassador'}
            </h1>
            <p className="text-muted-foreground max-w-2xl">
              Share the art of Colin Isaacs and earn commissions on every sale 
              made through your referral link. Your current commission rate is{' '}
              <span className="text-primary font-medium">
                {((stats?.commission_rate || 0.05) * 100).toFixed(0)}%
              </span>.
            </p>
          </motion.div>

          {/* Tabs */}
          <Tabs defaultValue="overview" className="space-y-6">
            <TabsList className="bg-muted/50 p-1 rounded-xl">
              <TabsTrigger value="overview" className="rounded-lg gap-2">
                <TrendingUp className="w-4 h-4" /> Overview
              </TabsTrigger>
              <TabsTrigger value="missions" className="rounded-lg gap-2">
                <Target className="w-4 h-4" /> Missions
              </TabsTrigger>
              <TabsTrigger value="resources" className="rounded-lg gap-2">
                <BookOpen className="w-4 h-4" /> Resources
              </TabsTrigger>
              <TabsTrigger value="points" className="rounded-lg gap-2">
                <Award className="w-4 h-4" /> Points
              </TabsTrigger>
            </TabsList>

            <TabsContent value="overview" className="space-y-8">
              {/* Stats Cards */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="grid grid-cols-2 md:grid-cols-4 gap-4"
              >
                <Card className="rounded-2xl">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-muted-foreground">Total Referrals</p>
                        <p className="text-3xl font-medium">{stats?.total_referrals || 0}</p>
                      </div>
                      <div className="w-10 h-10 rounded-full bg-blue-500/10 flex items-center justify-center">
                        <Users className="h-5 w-5 text-blue-500" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="rounded-2xl">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-muted-foreground">Total Sales</p>
                        <p className="text-3xl font-medium">{formatCurrency(stats?.total_sales)}</p>
                      </div>
                      <div className="w-10 h-10 rounded-full bg-green-500/10 flex items-center justify-center">
                        <TrendingUp className="h-5 w-5 text-green-500" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="rounded-2xl">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                    <p className="text-sm text-muted-foreground">Earned</p>
                    <p className="text-3xl font-medium">{formatCurrency(stats?.commission_earned)}</p>
                  </div>
                  <div className="w-10 h-10 rounded-full bg-amber-500/10 flex items-center justify-center">
                    <DollarSign className="h-5 w-5 text-amber-500" />
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="rounded-2xl">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Pending</p>
                    <p className="text-3xl font-medium">{formatCurrency(stats?.pending_commission)}</p>
                  </div>
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                    <Clock className="h-5 w-5 text-primary" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Referral Link Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            data-testid="referral-link-section"
          >
            <Card className="rounded-2xl mb-8">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Share2 className="h-5 w-5" />
                  Your Referral Link
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-col md:flex-row gap-4 mb-6">
                  <div className="flex-1">
                    <Input
                      value={referralLink}
                      readOnly
                      className="font-mono text-sm"
                    />
                  </div>
                  <Button onClick={copyReferralLink} className="rounded-full">
                    <Copy className="h-4 w-4 mr-2" /> Copy Link
                  </Button>
                </div>

                <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">Your Referral Code</p>
                    <div className="flex items-center gap-2">
                      <code className="px-3 py-1 bg-muted rounded-full font-mono text-lg">
                        {referralCode}
                      </code>
                      <Button variant="ghost" size="icon" onClick={copyReferralCode}>
                        <Copy className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  
                  <div className="flex gap-2">
                    <Button variant="outline" size="icon" onClick={fetchQrCode} className="rounded-full" title="Get QR Code">
                      <QrCode className="h-4 w-4" />
                    </Button>
                    <Button variant="outline" size="icon" onClick={shareOnFacebook} className="rounded-full">
                      <Facebook className="h-4 w-4" />
                    </Button>
                    <Button variant="outline" size="icon" onClick={shareOnTwitter} className="rounded-full">
                      <Twitter className="h-4 w-4" />
                    </Button>
                    <Button variant="outline" size="icon" onClick={shareByEmail} className="rounded-full">
                      <Mail className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Recent Activity */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="grid md:grid-cols-2 gap-8"
          >
            {/* Recent Referrals */}
            <Card className="rounded-2xl">
              <CardHeader>
                <CardTitle className="text-lg">Recent Referrals</CardTitle>
              </CardHeader>
              <CardContent>
                {referrals.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Gift className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>No referrals yet</p>
                    <p className="text-sm mt-1">Share your link to start earning!</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {referrals.slice(0, 5).map((ref, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                        <div>
                          <p className="font-medium">{ref.customer_name || "Customer"}</p>
                          <p className="text-xs text-muted-foreground">{formatDate(ref.created_at)}</p>
                        </div>
                        <div className="text-right">
                          <p className="font-medium text-primary">{formatCurrency(ref.order_total)}</p>
                          <p className="text-xs text-muted-foreground">
                            +{formatCurrency(ref.commission)} commission
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Payout History */}
            <Card className="rounded-2xl">
              <CardHeader>
                <CardTitle className="text-lg">Payout History</CardTitle>
              </CardHeader>
              <CardContent>
                {payouts.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <DollarSign className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>No payouts yet</p>
                    <p className="text-sm mt-1">Payouts are processed monthly</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {payouts.slice(0, 5).map((payout, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                        <div>
                          <p className="font-medium">{formatDate(payout.date)}</p>
                          <p className="text-xs text-muted-foreground">{payout.method}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{formatCurrency(payout.amount)}</span>
                          <Badge variant={payout.status === "paid" ? "default" : "secondary"}>
                            {payout.status === "paid" && <CheckCircle className="h-3 w-3 mr-1" />}
                            {payout.status}
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </motion.div>

          {/* Tips Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="mt-8"
          >
            <Card className="rounded-2xl bg-primary/5 border-primary/20">
              <CardContent className="pt-6">
                <h3 className="font-medium mb-4 flex items-center gap-2">
                  <TrendingUp className="h-5 w-5 text-primary" />
                  Tips to Maximize Your Earnings
                </h3>
                <div className="grid md:grid-cols-3 gap-4 text-sm">
                  <div className="p-4 bg-background rounded-lg">
                    <p className="font-medium mb-1">Share on Social Media</p>
                    <p className="text-muted-foreground">
                      Post about the collection with your unique link on Instagram, Facebook, or LinkedIn.
                    </p>
                  </div>
                  <div className="p-4 bg-background rounded-lg">
                    <p className="font-medium mb-1">Tell the Story</p>
                    <p className="text-muted-foreground">
                      Share Colin&apos;s story and the cultural significance of his artwork.
                    </p>
                  </div>
                  <div className="p-4 bg-background rounded-lg">
                    <p className="font-medium mb-1">Target Art Lovers</p>
                    <p className="text-muted-foreground">
                      Connect with galleries, interior designers, and art collectors in your network.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Quick Links */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="mt-8 flex flex-wrap gap-4"
          >
            <Link to="/products">
              <Button variant="outline" className="rounded-full">
                <ExternalLink className="h-4 w-4 mr-2" /> View Collection
              </Button>
            </Link>
            <Link to="/artist">
              <Button variant="outline" className="rounded-full">
                <ExternalLink className="h-4 w-4 mr-2" /> About the Artist
              </Button>
            </Link>
          </motion.div>
            </TabsContent>

            <TabsContent value="missions">
              <MissionsHub token={token} />
            </TabsContent>

            <TabsContent value="resources">
              <ResourceLibrary token={token} />
            </TabsContent>

            <TabsContent value="points">
              <PointsDashboard token={token} />
            </TabsContent>
          </Tabs>
        </div>
      </section>

      {/* QR Code Modal */}
      <Dialog open={showQrModal} onOpenChange={setShowQrModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <QrCode className="h-5 w-5" />
              Your Referral QR Code
            </DialogTitle>
            <DialogDescription>
              Share this QR code to let others easily access your referral link
            </DialogDescription>
          </DialogHeader>
          
          {qrData && (
            <div className="flex flex-col items-center gap-4 py-4">
              <div className="p-4 bg-white rounded-xl border shadow-sm">
                <img 
                  src={qrData.qr_data_url} 
                  alt="Referral QR Code" 
                  className="w-48 h-48"
                />
              </div>
              
              <div className="text-center">
                <p className="text-sm text-muted-foreground mb-1">Referral Code</p>
                <code className="px-3 py-1 bg-muted rounded-full font-mono text-lg">
                  {qrData.referral_code}
                </code>
              </div>
              
              <div className="flex gap-2 w-full">
                <Button onClick={downloadQrCode} className="flex-1">
                  <Download className="h-4 w-4 mr-2" />
                  Download QR
                </Button>
                <Button variant="outline" onClick={() => {
                  navigator.clipboard.writeText(qrData.referral_url);
                  toast.success("Link copied!");
                }} className="flex-1">
                  <Copy className="h-4 w-4 mr-2" />
                  Copy Link
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
};

export default PromoterDashboardPage;
